import React from 'react';

import cx from 'classnames';

import { getTrackingUrl } from '@xingternal/pages-tracking';
import { Contacts, ContactsSkeleton } from '../commonComponents';
import {
  ModuleHeader,
  usePageContext,
  UpsellBanner,
} from '@xingternal/pages-common';
import Error from '@xingternal/pages-warning';

import { normalizeData } from '../helpers';

import { useContactsQuery } from '../ContactsQuery.gql-types';

import styles from './ContactsContainer.css';

type ContactsContainerProps = {
  moduleType: string;
  pageSlug: string;
};

type RenderErrorProps = {
  refetch: () => void;
};

const RenderError = ({ refetch }: RenderErrorProps) => (
  <div data-testid={'errorContainer'}>
    <Error
      headerText="EP_ERROR_HEADER"
      bodyText="EP_ERROR_BODY"
      buttonText="EP_ERROR_RELOAD_CTA"
      onClick={() => refetch()}
    />
  </div>
);

const ContactsContainer = ({
  pageSlug,
  moduleType,
}: ContactsContainerProps) => {
  const { pageContext } = usePageContext();
  const upsellActive = pageContext?.upsellActive;

  const { data, loading, refetch } = useContactsQuery({
    variables: { id: pageSlug, limit: upsellActive ? 1 : 2 },
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  const { userList, currentUser, totalContacts } = normalizeData(data);

  const showMore =
    totalContacts > 2
      ? {
          to: getTrackingUrl({
            to: `${pageContext?.basePath}/${pageSlug}/contacts`,
            trackingKey: 'viewAllContacts',
          }),
          copyKey: 'EP_ALL_CONTACTS',
          testId: `${moduleType}_subpage_link`,
        }
      : null;

  if (loading && userList.length === 0) {
    return (
      <div className={styles.layout}>
        <div className={styles.flex}>
          <ModuleHeader
            headlineCopyKey="EP_CONTACTS_MODULE_TITLE"
            showMore={showMore}
          />
          <ContactsSkeleton />
        </div>
      </div>
    );
  }

  if (!loading && userList.length === 0) {
    return (
      <>
        <ModuleHeader
          editTestId="EDIT_BUTTON_ON_CONTACTS"
          headlineCopyKey="EP_CONTACTS_MODULE_TITLE"
          editTo={`${pageContext?.basePath}/${pageSlug}/edit/contacts`}
          showMore={showMore}
        />
        <RenderError refetch={refetch} />
      </>
    );
  }
  return (
    <div className={styles.layout} data-testid="CONTACTS_MODULE">
      <div className={styles.flex}>
        <ModuleHeader
          editTestId="EDIT_BUTTON_ON_CONTACTS"
          headlineCopyKey="EP_CONTACTS_MODULE_TITLE"
          editTo={`${pageContext?.basePath}/${pageSlug}/edit/contacts`}
          showMore={showMore}
        />

        <Contacts
          currentUser={currentUser}
          //@ts-ignore fix type user or tranform model
          users={userList}
          upsellActive={upsellActive}
          messageEntryPoint={'entity_pages_main_contact_message'}
          trackingModuleType="Main"
        />
      </div>
      {upsellActive ? (
        <div className={cx(styles.flex, styles.upsellContacts)}>
          <UpsellBanner
            headerCopyKey="EP_UPSELL_CONTACT_HEADER"
            bodyCopyKey="EP_UPSELL_CONTACT_BODY"
            moduleName="contactsmodule"
            column
          />
        </div>
      ) : null}
    </div>
  );
};

export default ContactsContainer;
